import { Button } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  CreditCard,
  FileStack,
  Package,
  PieChart as PieChartIcon,
  ShoppingBag,
  ShoppingCart,
  UserCircle,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { PermissionGate } from "@/modules/auth/presentation/ui/PermissionGate";
import { useCustomersQuery } from "@/modules/customers/presentation/api/queries";
import {
  useOrderSummaryQuery,
  useOrdersQuery,
} from "@/modules/orders/presentation/api/queries";
import {
  usePaymentSummaryQuery,
  usePaymentsQuery,
} from "@/modules/payments/presentation/api/queries";
import { useProductsQuery } from "@/modules/products/presentation/api/queries";
import { formatPriceLak } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";

const ORDER_STATUS_LABELS: Record<string, string> = {
  pending_payment: "ລໍຊຳລະ",
  confirmed: "ຢືນຢັນ",
  packing: "ກຳລັງເກັບ",
  shipped: "ສົ່ງແລ້ວ",
  completed: "ສຳເລັດ",
  return_in_transit: "ຕີກັບກຳລັງມາ",
  returned: "ຕີກັບ",
  cancelled: "ຍົກເລີກ",
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "ລໍຊຳລະ",
  paid: "ຊຳລະແລ້ວ",
  failed: "ລົ້ມເຫຼວ",
  partially_refunded: "ຄືນບາງສ່ວນ",
  refunded: "ຄືນເງິນ",
  cancelled: "ຍົກເລີກ",
};

export function DashboardPage() {
  const orderSummary = useOrderSummaryQuery();
  const paymentSummary = usePaymentSummaryQuery();
  const recentOrders = useOrdersQuery({
    limit: 5,
    offset: 0,
    sort: [{ field: "createdAt", dir: "desc" }],
  });
  const recentPayments = usePaymentsQuery({
    limit: 5,
    offset: 0,
    sort: [{ field: "createdAt", dir: "desc" }],
  });
  const productsCount = useProductsQuery({ limit: 1, offset: 0 });
  const customersCount = useCustomersQuery({ limit: 1, offset: 0 });

  return (
    <>
      <Header>
        <Breadcrumb items={[{ label: "ແຜງຄວບຄຸມ" }]} />
      </Header>
      <Main>
        <div className="mb-6">
          <h1 className="font-bold text-2xl tracking-tight">ແຜງຄວບຄຸມ</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            ພາບລວມລະບົບ ແລະ ຄຳສັ່ງຊື້ລ່າສຸດ
          </p>
        </div>

        {/* Stat cards — แถวที่ 1 */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <PermissionGate any={["orders:read"]} fallback={null}>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                  <ShoppingCart className="size-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">ຄຳສັ່ງຊື້ທັງໝົດ</p>
                  <p className="font-semibold text-xl">
                    {orderSummary.isLoading
                      ? "—"
                      : orderSummary.data != null
                        ? `${orderSummary.data.total} ອັນ`
                        : "—"}
                  </p>
                </div>
              </div>
              {orderSummary.data && (
                <p className="mt-2 text-muted-foreground text-sm">
                  ยอดรวม{" "}
                  {formatPriceLak(orderSummary.data.totalAmount, {
                    suffix: true,
                  })}
                </p>
              )}
              <Link to="/app/orders">
                <Button variant="ghost" size="sm" className="mt-3 -ml-2">
                  ເບິ່ງຄຳສັ່ງຊື້
                </Button>
              </Link>
            </div>
          </PermissionGate>

          <PermissionGate any={["orders:read"]} fallback={null}>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-amber-500/10">
                  <ShoppingCart className="size-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">ຄຳສັ່ງຊື້ລໍຊຳລະ</p>
                  <p className="font-semibold text-xl">
                    {orderSummary.isLoading
                      ? "—"
                      : orderSummary.data?.byStatus != null
                        ? String(
                            orderSummary.data.byStatus.pending_payment ?? 0,
                          )
                        : "—"}
                  </p>
                </div>
              </div>
              <Link to="/app/orders">
                <Button variant="ghost" size="sm" className="mt-3 -ml-2">
                  ເບິ່ງຄຳສັ່ງຊື້
                </Button>
              </Link>
            </div>
          </PermissionGate>

          <PermissionGate any={["payments:read"]} fallback={null}>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                  <CreditCard className="size-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">ການຊຳລະທັງໝົດ</p>
                  <p className="font-semibold text-xl">
                    {paymentSummary.isLoading
                      ? "—"
                      : paymentSummary.data != null
                        ? `${paymentSummary.data.total} ລາຍການ`
                        : "—"}
                  </p>
                </div>
              </div>
              {paymentSummary.data && (
                <p className="mt-2 text-muted-foreground text-sm">
                  ຊຳລະແລ້ວ{" "}
                  {formatPriceLak(paymentSummary.data.totalPaid, {
                    suffix: true,
                  })}
                </p>
              )}
              <Link to="/app/payments">
                <Button variant="ghost" size="sm" className="mt-3 -ml-2">
                  ເບິ່ງການຊຳລະ
                </Button>
              </Link>
            </div>
          </PermissionGate>

          <PermissionGate any={["catalog:read"]} fallback={null}>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                  <ShoppingBag className="size-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">ສິນຄ້າ (ທັງໝົດ)</p>
                  <p className="font-semibold text-xl">
                    {productsCount.isLoading
                      ? "—"
                      : productsCount.data?.meta != null
                        ? `${productsCount.data.meta.total} ລາຍການ`
                        : "—"}
                  </p>
                </div>
              </div>
              <Link to="/app/catalog/products">
                <Button variant="ghost" size="sm" className="mt-3 -ml-2">
                  ເບິ່ງສິນຄ້າ
                </Button>
              </Link>
            </div>
          </PermissionGate>
        </div>

        {/* Stat cards — แถวที่ 2: ລູກຄ້າ + ลิงก์ลัด */}
        <div className="mb-8 grid gap-4 lg:grid-cols-3">
          <PermissionGate any={["customers:read"]} fallback={null}>
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                  <UserCircle className="size-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-sm">ລູກຄ້າທັງໝົດ</p>
                  <p className="font-semibold text-xl">
                    {customersCount.isLoading
                      ? "—"
                      : customersCount.data?.meta != null
                        ? `${customersCount.data.meta.total} ຄົນ`
                        : "—"}
                  </p>
                </div>
              </div>
              <Link to="/app/customers">
                <Button variant="ghost" size="sm" className="mt-3 -ml-2">
                  ເບິ່ງລູກຄ້າ
                </Button>
              </Link>
            </div>
          </PermissionGate>

          <div className="rounded-xl border bg-card p-5 lg:col-span-2">
            <p className="mb-3 text-muted-foreground text-sm">ເຂົ້າເຖິງໄວ</p>
            <div className="flex flex-wrap gap-2">
              <PermissionGate any={["catalog:read"]} fallback={null}>
                <Link to="/app/catalog/products">
                  <Button variant="outline" size="sm">
                    <ShoppingBag className="mr-1.5 size-4" />
                    ສິນຄ້າ
                  </Button>
                </Link>
              </PermissionGate>
              <PermissionGate any={["orders:read"]} fallback={null}>
                <Link to="/app/orders">
                  <Button variant="outline" size="sm">
                    <ShoppingCart className="mr-1.5 size-4" />
                    ຄຳສັ່ງຊື້
                  </Button>
                </Link>
              </PermissionGate>
              <PermissionGate any={["payments:read"]} fallback={null}>
                <Link to="/app/reports">
                  <Button variant="outline" size="sm">
                    <PieChartIcon className="mr-1.5 size-4" />
                    ລາຍງານກຣາຟຊຳລະ
                  </Button>
                </Link>
              </PermissionGate>
              <PermissionGate any={["inventory:read"]} fallback={null}>
                <Link to="/app/inventory/stock">
                  <Button variant="outline" size="sm">
                    <Package className="mr-1.5 size-4" />
                    ສະຕ໋ອກ
                  </Button>
                </Link>
              </PermissionGate>
              <PermissionGate any={["cms:read"]} fallback={null}>
                <Link to="/app/cms/sections">
                  <Button variant="outline" size="sm">
                    <FileStack className="mr-1.5 size-4" />
                    ເນື້ອຫາ
                  </Button>
                </Link>
              </PermissionGate>
              <PermissionGate any={["settings:read"]} fallback={null}>
                <Link to="/app/settings">
                  <Button variant="outline" size="sm">
                    ຕັ້ງຄ່າ
                  </Button>
                </Link>
              </PermissionGate>
            </div>
          </div>
        </div>

        {/* ສະຖານະຄຳສັ່ງຊື້ (ຕາຕະລາງຍ່ອຍ) */}
        <PermissionGate any={["orders:read"]} fallback={null}>
          {orderSummary.data?.byStatus != null && (
            <section className="mb-8 rounded-xl border bg-card p-4">
              <h2 className="mb-3 font-semibold text-sm">ສະຖານະຄຳສັ່ງຊື້</h2>
              <div className="flex flex-wrap gap-4">
                {(
                  [
                    "pending_payment",
                    "confirmed",
                    "packing",
                    "shipped",
                    "completed",
                    "return_in_transit",
                    "returned",
                    "cancelled",
                  ] as const
                ).map((status) => (
                  <div
                    key={status}
                    className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2"
                  >
                    <span className="text-muted-foreground text-sm">
                      {ORDER_STATUS_LABELS[status]}:
                    </span>
                    <span className="font-medium">
                      {orderSummary.data?.byStatus[status] ?? 0}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </PermissionGate>

        {/* Recent orders + Recent payments — สองคอลัมน์บนจอใหญ่ */}
        <div className="grid gap-6 lg:grid-cols-2">
          <PermissionGate any={["orders:read"]} fallback={null}>
            <section className="rounded-xl border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b p-4">
                <h2 className="font-semibold text-lg">ຄຳສັ່ງຊື້ລ່າສຸດ</h2>
                <Link to="/app/orders">
                  <Button variant="outline" size="sm">
                    ທັງໝົດ
                  </Button>
                </Link>
              </div>
              <QueryState
                result={recentOrders}
                title="ກຳລັງໂຫຼດຄຳສັ່ງຊື້"
                description=""
                variant="fullscreen"
              >
                {recentOrders.data?.data &&
                recentOrders.data.data.length > 0 ? (
                  <div className="divide-y">
                    {recentOrders.data.data.map((order) => (
                      <Link
                        key={order.id}
                        to="/app/orders/$id"
                        params={{ id: order.id }}
                        className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-muted/50"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium">{order.code}</p>
                          <p className="text-muted-foreground text-sm">
                            {order.customerName ?? "—"} ·{" "}
                            {ORDER_STATUS_LABELS[order.status] ?? order.status}
                          </p>
                        </div>
                        <p className="shrink-0 font-medium text-muted-foreground text-sm">
                          {formatPriceLak(order.total, { suffix: true })}
                        </p>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground text-sm">
                    ຍັງບໍ່ມີຄຳສັ່ງຊື້
                  </div>
                )}
              </QueryState>
            </section>
          </PermissionGate>

          <PermissionGate any={["payments:read"]} fallback={null}>
            <section className="rounded-xl border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b p-4">
                <h2 className="font-semibold text-lg">ການຊຳລະລ່າສຸດ</h2>
                <Link to="/app/payments">
                  <Button variant="outline" size="sm">
                    ທັງໝົດ
                  </Button>
                </Link>
              </div>
              <QueryState
                result={recentPayments}
                title="ກຳລັງໂຫຼດການຊຳລະ"
                description=""
                variant="fullscreen"
              >
                {recentPayments.data?.data &&
                recentPayments.data.data.length > 0 ? (
                  <div className="divide-y">
                    {recentPayments.data.data.map((payment) => (
                      <Link
                        key={payment.id}
                        to="/app/payments/$id"
                        params={{ id: payment.id }}
                        className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-muted/50"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {payment.orderCode ?? payment.orderId}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {PAYMENT_STATUS_LABELS[payment.status] ??
                              payment.status}{" "}
                            · {formatPriceLak(payment.amount, { suffix: true })}
                          </p>
                        </div>
                        <p className="shrink-0 text-muted-foreground text-sm">
                          {payment.paidAt
                            ? new Date(payment.paidAt).toLocaleDateString(
                                "lo-LA",
                              )
                            : "—"}
                        </p>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground text-sm">
                    ຍັງບໍ່ມີລາຍການຊຳລະ
                  </div>
                )}
              </QueryState>
            </section>
          </PermissionGate>
        </div>
      </Main>
    </>
  );
}
